import { create } from "zustand";
import { api, ApiError, type GuessResponse } from "@app/api/client.ts";
import type { PlayProgress, PublicRound } from "@shared/types";

interface GameState {
  round: PublicRound | null;
  guesses: PlayProgress[];
  totalScore: number | null;
  percentile: number | null;
  currentStreak: number | null;
  shareId: string | null;
  position: number;
  completed: boolean;
  loading: boolean;
  error: string | null;
  advance: () => void;
  load: () => Promise<void>;
  submit: (pin: { lat: number; lng: number }) => Promise<GuessResponse>;
  markCompleted: (response: GuessResponse) => void;
  resetLocal: () => void;
}

function messageFor(error: unknown): string {
  if (error instanceof ApiError) {
    switch (error.code) {
      case "no_round":
        return "Hoy no hay ronda disponible. Vuelve en un rato.";
      case "round_not_current":
        return "La ronda ha cambiado. Recarga para jugar la nueva.";
      case "out_of_order":
        return "Esa pista no es la siguiente. Recarga la partida.";
      case "rate_limited":
        return "Vas demasiado rápido. Espera unos segundos.";
      case "offline":
        return "Sin conexión, vuelve a intentarlo.";
      default:
        return "No hemos podido conectar. Inténtalo de nuevo.";
    }
  }
  return "No hemos podido conectar. Inténtalo de nuevo.";
}

export const useGame = create<GameState>((set, get) => ({
  round: null,
  guesses: [],
  totalScore: null,
  percentile: null,
  currentStreak: null,
  shareId: null,
  position: 1,
  completed: false,
  loading: false,
  error: null,
  advance: () => set((s) => ({ position: Math.min(5, s.position + 1) })),
  load: async () => {
    set({ loading: true, error: null });
    try {
      const current = await api.playCurrent();
      set({
        round: current.round,
        guesses: current.guesses,
        totalScore: current.totalScore,
        percentile: current.percentile,
        currentStreak: current.currentStreak,
        shareId: current.shareId,
        position: current.nextPosition ?? 5,
        completed: current.state === "completada",
        loading: false,
      });
    } catch (error) {
      set({ loading: false, error: messageFor(error) });
    }
  },
  submit: async (pin) => {
    const { round, position, guesses } = get();
    if (!round) throw new Error("sin ronda");
    const response = await api.guess({
      roundId: round.id,
      position,
      lat: pin.lat,
      lng: pin.lng,
    });
    const nextGuesses: PlayProgress[] = [
      ...guesses.filter((g) => g.position !== position),
      { ...response.result, guessLat: pin.lat, guessLng: pin.lng },
    ].sort((a, b) => a.position - b.position);
    set({
      guesses: nextGuesses,
      totalScore: response.totalScore,
      percentile: response.percentile,
      currentStreak: response.currentStreak,
      shareId: response.shareId,
      completed: response.completed,
    });
    return response;
  },
  markCompleted: (response) =>
    set({
      completed: response.completed,
      totalScore: response.totalScore,
      percentile: response.percentile,
      shareId: response.shareId,
      currentStreak: response.currentStreak,
    }),
  resetLocal: () =>
    set({
      guesses: [],
      totalScore: null,
      percentile: null,
      shareId: null,
      position: 1,
      completed: false,
      error: null,
    }),
}));
